import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { UserPlus, Trash2, Save, Loader2, User } from 'lucide-react';

export default function Settings() {
  const { user } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', display_name: '' });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { users: userList } = await api.listUsers();
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
    setIsLoading(false);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.register(newUser.username, newUser.password, newUser.display_name);
      setNewUser({ username: '', password: '', display_name: '' });
      setShowAddUser(false);
      await loadUsers();
    } catch (error) {
      alert('Failed to create user: ' + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (confirm('Delete this user?')) {
      try {
        await api.request(`/auth/users/${userId}`, { method: 'DELETE' });
        await loadUsers();
      } catch (error) {
        alert('Failed to delete user: ' + error.message);
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage your account and application settings</p>
      </div>

      {/* Current user */}
      <Card>
        <CardHeader>
          <CardTitle>Current User</CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User size={24} className="text-primary" />
            </div>
            <div>
              <p className="font-medium">{user?.username}</p>
              <p className="text-sm text-muted-foreground">{user?.display_name || 'No display name'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>Add or remove users</CardDescription>
          </div>
          <Button size="sm" onClick={() => setShowAddUser(!showAddUser)}>
            <UserPlus size={16} className="mr-2" />
            Add User
          </Button>
        </CardHeader>
        <CardContent>
          {showAddUser && (
            <form onSubmit={handleAddUser} className="mb-4 p-4 bg-muted rounded-lg space-y-3">
              <Input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                placeholder="Username"
                required
              />
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Password"
                required
              />
              <Input
                type="text"
                value={newUser.display_name}
                onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })}
                placeholder="Display name (optional)"
              />
              <div className="flex gap-2">
                <Button type="submit" size="sm">
                  <Save size={14} className="mr-2" />
                  Create User
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowAddUser(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-background flex items-center justify-center">
                      <span className="text-sm font-medium">{u.username.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{u.username}</p>
                      <p className="text-xs text-muted-foreground">
                        {u.display_name || 'No display name'} · Joined {new Date(u.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {u.id !== user?.id && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteUser(u.id)}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>System configuration status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { label: 'DeepSeek API', status: 'Configured' },
              { label: 'Backblaze B2', status: 'Configured' },
              { label: 'Cloudflare D1', status: 'Connected' }
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <span className="text-sm">{item.label}</span>
                <span className="text-sm text-muted-foreground">{item.status}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
